import React, { useState, useEffect, useMemo } from 'react';
import { useLocale } from '@platform/core';
import { Project, ProjectStatus, BudgetSubject, BudgetSection, IntervalType, MeasureType, SubjectType } from '../../types/budget.types';
import { useBudget } from '../../contexts/BudgetContext';

interface ProjectFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  project?: Project;
}

interface FormData {
  name: string;
  description: string;
  client: string;
  projectManager: string;
  manager: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  status: ProjectStatus;
  budget: number;
  currency: string;
  intervalType: IntervalType;
  measures: MeasureType[];
  subjects: BudgetSubject[];
  sections: BudgetSection[];
}

// Default subjects for a new project
const defaultSubjects: BudgetSubject[] = [
  { id: 'income', name: 'הכנסות', type: 'credit', order: 0 },
  { id: 'expenses', name: 'הוצאות', type: 'debit', order: 1 },
  { id: 'profit', name: 'רווח', type: 'calculated', order: 2 },
];

const defaultMeasures: MeasureType[] = ['actual', 'budget'];

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  const { t } = useLocale();
  const { createProject, updateProject, currentCompany } = useBudget();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    client: '',
    projectManager: '',
    manager: '',
    contractNumber: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'active',
    budget: 0,
    currency: 'ILS',
    intervalType: 'monthly',
    measures: defaultMeasures,
    subjects: [...defaultSubjects],
    sections: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'structure'>('general');

  const isEditMode = !!project;

  // Initialize form from project when editing
  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || '',
        client: project.client,
        projectManager: project.projectManager,
        manager: project.manager,
        contractNumber: project.contractNumber,
        startDate: project.startDate,
        endDate: project.endDate,
        status: project.status,
        budget: project.budget,
        currency: project.currency || 'ILS',
        intervalType: project.intervalType || 'monthly',
        measures: project.measures as MeasureType[] || defaultMeasures,
        subjects: project.subjects || [...defaultSubjects],
        sections: project.sections || [],
      });
    } else {
      // Reset for new project
      setFormData({
        name: '',
        description: '',
        client: '',
        projectManager: '',
        manager: '',
        contractNumber: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'active',
        budget: 0,
        currency: 'ILS',
        intervalType: 'monthly',
        measures: defaultMeasures,
        subjects: [...defaultSubjects],
        sections: [],
      });
    }
    setError(null);
    setActiveTab('general');
  }, [project, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  // Subject/Section management
  const addSubject = (type: SubjectType) => {
    const name = prompt(t('settings.enterSubjectName') || 'Enter subject name:');
    if (!name) return;

    const newSubject: BudgetSubject = {
      id: generateId(),
      name,
      type,
      order: formData.subjects.length,
    };

    setFormData(prev => ({
      ...prev,
      subjects: [...prev.subjects, newSubject],
    }));
  };

  const updateSubject = (subjectId: string, updates: Partial<BudgetSubject>) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.map(s =>
        s.id === subjectId ? { ...s, ...updates } : s
      ),
    }));
  };

  const removeSubject = (subjectId: string) => {
    if (!confirm(t('actions.confirmDelete') || 'Are you sure?')) return;

    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s.id !== subjectId),
      sections: prev.sections.filter(s => s.subjectId !== subjectId),
    }));
  };

  const addSection = (subjectId: string) => {
    const name = prompt(t('settings.enterSectionName') || 'Enter section name:');
    if (!name) return;

    const newSection: BudgetSection = {
      id: generateId(),
      name,
      subjectId,
      order: formData.sections.filter(s => s.subjectId === subjectId).length,
    };

    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
  };

  const updateSection = (sectionId: string, updates: Partial<BudgetSection>) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      ),
    }));
  };

  const removeSection = (sectionId: string) => {
    if (!confirm(t('actions.confirmDelete') || 'Are you sure?')) return;

    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId),
    }));
  };

  const moveSubject = (subjectId: string, direction: 'up' | 'down') => {
    const subjects = [...formData.subjects];
    const index = subjects.findIndex(s => s.id === subjectId);
    if (direction === 'up' && index > 0) {
      [subjects[index - 1], subjects[index]] = [subjects[index], subjects[index - 1]];
    } else if (direction === 'down' && index < subjects.length - 1) {
      [subjects[index], subjects[index + 1]] = [subjects[index + 1], subjects[index]];
    }
    subjects.forEach((s, i) => s.order = i);
    setFormData(prev => ({ ...prev, subjects }));
  };

  // Group sections by subject
  const sectionsBySubject = useMemo(() => {
    const grouped: Record<string, BudgetSection[]> = {};
    formData.sections.forEach(section => {
      if (!grouped[section.subjectId]) {
        grouped[section.subjectId] = [];
      }
      grouped[section.subjectId].push(section);
    });
    return grouped;
  }, [formData.sections]);

  const getSubjectTypeLabel = (type: SubjectType) => {
    switch (type) {
      case 'credit': return t('settings.credit') || 'זכות';
      case 'debit': return t('settings.debit') || 'חובה';
      case 'calculated': return t('settings.calculated') || 'מחושב';
      default: return type;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError(t('validation.required') || 'Name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const projectData = {
        name: formData.name,
        description: formData.description,
        organizationId: currentCompany?.id || '',
        client: formData.client,
        projectManager: formData.projectManager,
        manager: formData.manager,
        contractNumber: formData.contractNumber,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
        budget: formData.budget,
        currency: formData.currency,
        intervalType: formData.intervalType,
        measures: formData.measures,
        subjects: formData.subjects,
        sections: formData.sections,
        goals: [], // Goals are now embedded in sections as incomePartGoal
      };

      if (isEditMode && project) {
        await updateProject(project.id, projectData);
      } else {
        await createProject(projectData);
      }
      onClose();
    } catch (err: any) {
      setError(err.message || t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? t('common.edit') : t('common.add')} {t('budget.projects.title')}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('budget.projects.generalInfo') || 'מידע כללי'}
          </button>
          <button
            className={`tab ${activeTab === 'structure' ? 'active' : ''}`}
            onClick={() => setActiveTab('structure')}
          >
            {t('settings.subjects') || 'נושאים וסעיפים'}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            {activeTab === 'general' && (
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>{t('budget.projects.name')} *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>{t('common.details')}</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={2}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('budget.projects.client')}</label>
                    <input
                      type="text"
                      name="client"
                      value={formData.client}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('budget.projects.contract')}</label>
                    <input
                      type="text"
                      name="contractNumber"
                      value={formData.contractNumber}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('budget.projects.projectManager')}</label>
                    <input
                      type="text"
                      name="projectManager"
                      value={formData.projectManager}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('budget.projects.manager')}</label>
                    <input
                      type="text"
                      name="manager"
                      value={formData.manager}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('budget.projects.startDate')}</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('budget.projects.endDate')}</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('budget.projects.status')}</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="active">{t('budget.projects.statusActive')}</option>
                      <option value="completed">{t('budget.projects.statusCompleted')}</option>
                      <option value="future">{t('budget.projects.statusFuture')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('budget.projects.budget')}</label>
                    <input
                      type="number"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('settings.currency') || 'מטבע'}</label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                    >
                      <option value="ILS">₪ ILS</option>
                      <option value="USD">$ USD</option>
                      <option value="EUR">€ EUR</option>
                      <option value="GBP">£ GBP</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('settings.interval') || 'תדירות'}</label>
                    <select
                      name="intervalType"
                      value={formData.intervalType}
                      onChange={handleChange}
                    >
                      <option value="daily">{t('settings.daily') || 'יומי'}</option>
                      <option value="weekly">{t('settings.weekly') || 'שבועי'}</option>
                      <option value="monthly">{t('settings.monthly') || 'חודשי'}</option>
                      <option value="quarterly">{t('settings.quarterly') || 'רבעוני'}</option>
                      <option value="yearly">{t('settings.yearly') || 'שנתי'}</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'structure' && (
              <div className="form-section">
                <p className="section-description">
                  {t('settings.subjectsDescription') || 'הגדר את מבנה הנושאים והסעיפים של הפרויקט'}
                </p>

                <div className="subjects-tree">
                  {formData.subjects.map((subject, index) => (
                    <div key={subject.id} className={`subject-node type-${subject.type}`}>
                      <div className="subject-header">
                        <span className="subject-name">
                          {subject.name} ({getSubjectTypeLabel(subject.type)})
                        </span>
                        <div className="subject-actions">
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => moveSubject(subject.id, 'up')}
                            disabled={index === 0}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => moveSubject(subject.id, 'down')}
                            disabled={index === formData.subjects.length - 1}
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => addSection(subject.id)}
                            title={t('settings.addSection')}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-danger"
                            onClick={() => removeSubject(subject.id)}
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      <div className="sections-list">
                        {(sectionsBySubject[subject.id] || []).map(section => (
                          <div key={section.id} className="section-row">
                            <span className="section-name">└ {section.name}</span>
                            <div className="section-goal">
                              <input
                                type="number"
                                value={section.incomePartGoal || 0}
                                onChange={e => updateSection(section.id, {
                                  incomePartGoal: parseFloat(e.target.value) || 0
                                })}
                                min="0"
                                max="100"
                                step="0.1"
                              />
                              <span>%</span>
                            </div>
                            <button
                              type="button"
                              className="btn-icon btn-danger"
                              onClick={() => removeSection(section.id)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        {(!sectionsBySubject[subject.id] || sectionsBySubject[subject.id].length === 0) && (
                          <div className="section-row empty">
                            <button
                              type="button"
                              className="btn-link"
                              onClick={() => addSection(subject.id)}
                            >
                              + {t('settings.addFirstSection') || 'הוסף סעיף'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="add-subject-buttons">
                    <button type="button" className="btn btn-add" onClick={() => addSubject('credit')}>
                      + {t('settings.addCredit') || 'הוסף זכות'}
                    </button>
                    <button type="button" className="btn btn-add" onClick={() => addSubject('debit')}>
                      + {t('settings.addDebit') || 'הוסף חובה'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? t('general.saving') : (isEditMode ? t('common.update') : t('common.add'))}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-content.large {
          max-width: 800px;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--color-border);
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.125rem;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-secondary);
        }

        .modal-tabs {
          display: flex;
          border-bottom: 1px solid var(--color-border);
          padding: 0 1.5rem;
        }

        .tab {
          padding: 0.75rem 1rem;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
        }

        .tab:hover {
          color: var(--color-text);
        }

        .tab.active {
          color: var(--color-primary);
          border-bottom-color: var(--color-primary);
        }

        .modal-form {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }

        .modal-body {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .error-message {
          padding: 0.75rem;
          background: #fee2e2;
          color: #991b1b;
          border-radius: var(--radius);
          margin-bottom: 1rem;
        }

        .form-section {
          margin-bottom: 1.5rem;
        }

        .section-description {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          margin: 0 0 1rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.875rem;
          background: var(--color-background);
        }

        .subjects-tree {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .subject-node {
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          overflow: hidden;
        }

        .subject-node.type-credit { border-color: #22c55e; }
        .subject-node.type-debit { border-color: #ef4444; }
        .subject-node.type-calculated { border-color: #3b82f6; }

        .subject-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem;
          background: var(--color-background);
        }

        .subject-name {
          font-weight: 500;
        }

        .subject-actions {
          display: flex;
          gap: 0.25rem;
        }

        .sections-list {
          border-top: 1px solid var(--color-border);
        }

        .section-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem 0.5rem 1.5rem;
          border-top: 1px solid var(--color-border);
        }

        .section-row:first-child {
          border-top: none;
        }

        .section-row.empty {
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
        }

        .section-name {
          flex: 1;
          font-size: 0.875rem;
        }

        .section-goal {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .section-goal input {
          width: 60px;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          text-align: center;
          font-size: 0.8rem;
        }

        .section-goal span {
          font-size: 0.8rem;
          color: var(--color-text-secondary);
        }

        .add-subject-buttons {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .btn-add {
          flex: 1;
          padding: 0.5rem;
          border: 1px dashed var(--color-border);
          border-radius: var(--radius);
          background: none;
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .btn-add:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }

        .btn-icon {
          width: 24px;
          height: 24px;
          padding: 0;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          cursor: pointer;
          font-size: 0.75rem;
        }

        .btn-icon:hover {
          background: var(--color-background);
        }

        .btn-icon:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .btn-icon.btn-danger:hover {
          background: #fee2e2;
          border-color: #ef4444;
          color: #ef4444;
        }

        .btn-link {
          background: none;
          border: none;
          color: var(--color-primary);
          cursor: pointer;
          font-size: 0.875rem;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--color-border);
          background: var(--color-background);
        }

        .btn {
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: var(--color-primary);
          color: white;
          border: none;
        }

        .btn-secondary {
          background: var(--color-surface);
          color: var(--color-text);
          border: 1px solid var(--color-border);
        }

        @media (max-width: 480px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
